import React, { useState, useEffect } from 'react';
import ClientButton from '../../components/ClientButton';
import { addNewServiceAddressesApi, getAllServiceAddressesApi } from '../../redux/Reducers/ServiceAddressesReducer';
import { useDispatch, useSelector } from 'react-redux';
import calendar_icon from '../../icons/icons-checkmark-round-green.svg';
import { toast } from 'react-hot-toast';
import { AddressAutofill } from '@mapbox/search-js-react';
import { isNull } from 'lodash';
import partyPopper from '../../images/party-popper.svg';
import Loader from '../../components/Loader';
import ModalBasic from '../../components/Modal';
import { useNavigate } from 'react-router-dom';
import { getProfileDataApi } from '../../redux/Reducers/ProfileReducer';
import { updateProfileApi } from '../../redux/Reducers/MyAccountReducer';
import CheckmarkIcon from '../../components/Icons/CheckmarkIcon';
import PhoneField from '../../components/PhoneField';
const MAPBOX_API_TOKEN =
  import.meta.env.VITE_TENANT_NAME === 'laundrycare'
    ? import.meta.env.VITE_LAUNDRYCARE_MAPBOX_API_TOKEN
    : import.meta.env.VITE_TENANT_NAME === 'pushlaundry'
    ? import.meta.env.VITE_PUSHLAUNDRY_MAPBOX_API_TOKEN
    : '';
const tenant = import.meta.env.VITE_TENANT_NAME;
export default function WelcomeModalNewAddress({ cancel = false, setModalOpen, modalOpen, data }) {
  const { profileData } = useSelector((state) => state.profile);
  const tenantName = import.meta.env.VITE_TENANT_NAME;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [phone, setPhone] = useState({ phone_number: profileData?.phone_numbers[0]?.phone_number || '' });
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [addressData, setAddressData] = useState({
    name: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
  });
  const [loading, setLoading] = useState(false);
  const [addressAdded, setAddressAdded] = useState(false);
  useEffect(() => {
    // If profile previously had an address suggestion in `data`, seed the form
    if (data?.address) {
      setAddressData((prev) => ({ ...prev, ...data.address }));
    }
  }, [data]);
  const addressChangeFn = (data) => {
    setAddressData((prev) => ({
      ...prev,
      address_line_1: data.features[0].properties.address_line1,
      address_line_2: data.features[0].properties.address_line2,
      city: data.features[0].properties.address_level2,
      state: data.features[0].properties.address_level1,
      postal_code: data.features[0].properties.postcode,
    }));
  };

  const validateAddressFn = (address) => {
    const fields = ['name', 'address_line_1', 'city', 'state', 'postal_code'];
    for (const field of fields) {
      if (!address[field] || address[field] === '') {
        toast.error(`Please enter a ${field.replace('_', ' ')}`);
        return false;
      }
    }
    return true;
  };
  const handleChange = (e) => {
    const { name, value } = e.target; // value already normalized: +1XXXXXXXXXX
    setPhone((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitAddress = (e) => {
    e.preventDefault();
    const payload = {
      name: addressData.name,
      address_line_1: addressData.address_line_1,
      address_line_2: addressData.address_line_2,
      city: addressData.city,
      state: addressData.state,
      postal_code: addressData.postal_code,
    };

    if (!validateAddressFn(payload)) return;

    if (phone?.phone_number == '' || phone?.phone_number?.length < 12) {
      setPhoneNumberError('Please enter a valid phone number');
      return;
    }

    setPhoneNumberError('');
    setLoading(true);

    const currentPhone = profileData?.phone_numbers?.[0]?.phone_number || '';
    const phoneChanged = phone?.phone_number && phone?.phone_number !== currentPhone;

    const runAddAddress = () => {
      const create = dispatch(addNewServiceAddressesApi(payload));
      create.then((res) => {
        if (res?.status === 200 || res?.status === 201) {
          setLoading(false);
          onCancel();
          setAddressAdded(true);
          dispatch(getAllServiceAddressesApi());
          toast.success('New address added successfully');
        } else {
          setLoading(false);
          if (res?.message == `The address ${addressData?.name} does not fall in any active service area.`) {
            toast.error(
              'The address you entered is currently outside our active service areas. Please double-check your address or contact our support team for assistance.'
            );
          } else {
            toast.error(res?.message || 'Something went wrong');
          }
        }
      });
    };

    if (phoneChanged) {
      // First update profile phone, then add address on success
      const updatePayload = { phone_number: { phone_number: phone?.phone_number } };
      const upd = updateProfileApi(updatePayload);
      upd?.then((ures) => {
        if (ures?.status === 200 || ures?.status === 201 || ures?.status === 204) {
          // refresh profile data
          dispatch(getProfileDataApi(false));
          // now add address
          runAddAddress();
        } else {
          setLoading(false);
          toast.error(ures?.message || 'Failed to update phone number');
        }
      });
    } else {
      // phone didn't change; just add address
      runAddAddress();
    }
  };

  const onCancel = () => {
    setModalOpen(false);
  };

  return (
    <>
      <div className='py-2 sm:px-5 sm:py-4'>
        <ModalBasic modalOpen={modalOpen} setModalOpen={setModalOpen} classes='max-w-2xl'>
          {loading ? (
            <Loader text='processing...' classes='h-40' />
          ) : !addressAdded ? (
            <>
              <div className='flex'>
                <div className='mx-6'>
                  <div className='mb-2 flex'>
                    <img src={partyPopper} alt='sort none' className='mr-2 h-6 w-6' />
                    Welcome, {data?.first_name}! Just one more step to setup your account.
                  </div>
                  <p>
                    Enter your address and phone number so we can show you accurate pricing and services available in
                    your area.
                  </p>
                </div>
              </div>
              <div className='m-auto  mt-5 max-w-5xl rounded-md  bg-white p-5'>
                <form onSubmit={handleSubmitAddress}>
                  <p className='go-textblacknormal-16'>Enter Your Address</p>
                  <div className='grid gap-5 pt-4 pb-10 md:grid-cols-3'>
                    <div>
                      <div>
                        <label className='go-label-16 mb-2 block' htmlFor='name'>
                          Label
                        </label>
                        <input
                          name='name'
                          className={`go-inputtext-16 ${isNull(addressData?.name) ? 'border-red-500' : ''}`}
                          type='text'
                          value={addressData?.name || ''}
                          onChange={(e) => setAddressData((prev) => ({ ...prev, name: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div>
                      <div>
                        <label className='go-label-16 mb-2 block' htmlFor='address_line_1'>
                          Street Address
                        </label>
                        <AddressAutofill
                          accessToken={MAPBOX_API_TOKEN}
                          options={{
                            language: 'en',
                            country: 'US',
                          }}
                          onRetrieve={addressChangeFn}
                        >
                          <input
                            name='address_line_1'
                            className={`go-inputtext-16 ${isNull(addressData?.address_line_1) ? 'border-red-500' : ''}`}
                            type='text'
                            placeholder='Address'
                            autoComplete='address-line1'
                            value={addressData?.address_line_1 || ''}
                            onChange={(e) => setAddressData((prev) => ({ ...prev, address_line_1: e.target.value }))}
                          />
                        </AddressAutofill>
                      </div>
                    </div>

                    <div>
                      <div>
                        <label className='go-label-16 mb-2 block' htmlFor='address_line_2'>
                          Apartment or suite number
                        </label>
                        <input
                          name='address_line_2'
                          className={`go-inputtext-16`}
                          type='text'
                          placeholder='Apartment number'
                          autoComplete='address-line2'
                          value={addressData?.address_line_2 || ''}
                          onChange={(e) => setAddressData((prev) => ({ ...prev, address_line_2: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div>
                      <div>
                        <label className='go-label-16 mb-2 block' htmlFor='city'>
                          City
                        </label>
                        <input
                          name='city'
                          className={`go-inputtext-16 ${isNull(addressData?.city) ? 'border-red-500' : ''}`}
                          type='text'
                          placeholder='City'
                          autoComplete='address-level2'
                          value={addressData?.city || ''}
                          onChange={(e) => setAddressData((prev) => ({ ...prev, city: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className='flex space-x-2'>
                      <div className='flex-1'>
                        <div>
                          <label className='go-label-16 mb-2 block' htmlFor='state'>
                            State
                          </label>
                          <input
                            name='state'
                            className={`go-inputtext-16 ${isNull(addressData?.state) ? 'border-red-500' : ''}`}
                            type='text'
                            placeholder='State'
                            autoComplete='address-level1'
                            value={addressData?.state || ''}
                            onChange={(e) => setAddressData((prev) => ({ ...prev, state: e.target.value }))}
                          />
                        </div>
                      </div>
                      <div className='flex-1'>
                        <div>
                          <label className='go-label-16 mb-2 block' htmlFor='postal_code'>
                            Zip Code
                          </label>
                          <input
                            name='postal_code'
                            className={`go-inputtext-16 ${isNull(addressData?.postal_code) ? 'border-red-500' : ''}`}
                            type='number'
                            placeholder='Postcode'
                            autoComplete='postal-code'
                            value={addressData?.postal_code || ''}
                            onChange={(e) => setAddressData((prev) => ({ ...prev, postal_code: e.target.value }))}
                            maxLength={5}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className='go-textblacknormal-16'>Enter Your Phone Number</p>{' '}
                  <div className='grid gap-5 pt-4 md:grid-cols-3'>
                    <div className='relative mb-1 flex-row space-y-1.5'>
                      <PhoneField
                        label='Phone'
                        required={true}
                        value={phone.phone_number}
                        onChange={handleChange}
                        error={phoneNumberError}
                      />
                    </div>
                  </div>
                  <p className=' text-xs text-gray-500'>
                    You agree to receive service and marketing auto-sent texts from{' '}
                    {tenantName == 'laundrycare' ? 'Laundry Care' : 'Push Laundry'}. <br></br>Opt-out anytime on your
                    “My Account” page or text “STOP”. Message & data rates may apply.
                  </p>
                  <div className='flex py-4 pb-10'>
                    <ClientButton variant='primary' text='Save' classes='mr-2 w-full sm:w-auto' type='submit' />
                    <ClientButton
                      variant='secondary'
                      text='Cancel'
                      classes='mr-2  w-full sm:w-auto'
                      onClick={(e) => {
                        onCancel();
                      }}
                    />
                  </div>
                </form>
              </div>
            </>
          ) : (
            <>
              <div className='flex'>
                <div className='mx-6 '>
                  <div className='mb-2 flex'>
                    <img src={calendar_icon} alt='tick' className='mr-2 h-5 w-5' />
                    All done!
                  </div>
                  <p>You're ready to go. Create your first laundry order today!</p>
                </div>
              </div>
              <div className='m-auto  mt-5 max-w-5xl rounded-md  bg-white p-5'>
                <div>
                  <ClientButton
                    variant='primary'
                    text='Create Order'
                    classes='mr-2'
                    onClick={() => navigate('/create-new-order')}
                  />
                </div>
              </div>
            </>
          )}
        </ModalBasic>
      </div>
    </>
  );
}
